import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Factory, FileBadge2, HelpCircle, Loader2, X } from 'lucide-react';
import type { ProductFormData } from '@/modelos/productos/Producto';
import type { FormError } from '@/hooks/productos/useProductoFormulario';
import Tooltip from '@/contasis/components/Tooltip/Tooltip';
import { Input, Select } from '@/contasis';
import { clasificacionesService } from '@/services/api/clasificaciones.service';
import type { ClasificacionItem } from '@/services/api/clasificaciones.service';
import { usePopoverPosition } from '@/hooks/usePopoverPosition';


interface ProductCodesFieldProps {
  formData: ProductFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProductFormData>>;
  errors: FormError;
  isFieldVisible: (fieldId: string) => boolean;
  isFieldRequired: (fieldId: string) => boolean;
  onBlur?: () => void;
  showCheck?: boolean;
  renderCheck?: (className?: string) => React.ReactNode;
}

export const ProductFactoryCodeField: React.FC<ProductCodesFieldProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  onBlur,
  showCheck,
  renderCheck
}) => {
  if (!isFieldVisible('codigoFabrica')) return null;

  return (
    <Input
      id="codigoFabrica"
      label="Código de fábrica"
      required={isFieldRequired('codigoFabrica')}
      value={formData.codigoFabrica}
      onChange={(e) => setFormData(prev => ({ ...prev, codigoFabrica: e.target.value }))}
      onBlur={onBlur}
      leftIcon={<Factory className="w-3.5 h-3.5" />}
      rightIcon={showCheck ? renderCheck?.() : undefined}
      error={errors.codigoFabrica}
      size="small"
      placeholder="Código del fabricante"
      className="font-mono"
    />
  );
};

const buildCodigoSunatLabel = (codigo?: string, nombre?: string): string => {
  if (!codigo) return '';
  return nombre ? `${codigo} · ${nombre}` : codigo;
};

export const ProductSunatCodeField: React.FC<ProductCodesFieldProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  onBlur,
  showCheck,
  renderCheck
}) => {
  const [query, setQuery] = useState(() => buildCodigoSunatLabel(formData.codigoSunat, formData.codigoSunatNombre));
  const [open, setOpen] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [resultados, setResultados] = useState<ClasificacionItem[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClose = useCallback(() => setOpen(false), []);
  const { triggerRef, popoverRef, position } = usePopoverPosition({
    open,
    onClose: handleClose,
    minWidth: 260,
    repositionKey: `${buscando}-${resultados.length}`,
  });

  useEffect(() => {
    setQuery(buildCodigoSunatLabel(formData.codigoSunat, formData.codigoSunatNombre));
  }, [formData.codigoSunat, formData.codigoSunatNombre]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  if (!isFieldVisible('codigoSunat')) return null;

  const handleChange = (value: string) => {
    setQuery(value);
    setFormData(prev => ({ ...prev, codigoSunat: '', codigoSunatNombre: '' }));

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setResultados([]);
      setOpen(false);
      setBuscando(false);
      return;
    }

    setOpen(true);
    setBuscando(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await clasificacionesService.buscar(trimmed, 1, 20);
        setResultados(res.ok ? res.data ?? [] : []);
      } catch {
        setResultados([]);
      } finally {
        setBuscando(false);
      }
    }, 300);
  };

  const handleClear = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQuery('');
    setResultados([]);
    setBuscando(false);
    setOpen(false);
    setFormData(prev => ({ ...prev, codigoSunat: '', codigoSunatNombre: '' }));
    document.getElementById('codigoSunat')?.focus();
  };

  const handleSelect = (item: ClasificacionItem) => {
    setFormData(prev => ({ ...prev, codigoSunat: item.idProducto, codigoSunatNombre: item.producto }));
    setQuery(buildCodigoSunatLabel(item.idProducto, item.producto));
    setResultados([]);
    setOpen(false);
    onBlur?.();
  };

  const dropdown = open
    ? createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[9999] max-h-64 overflow-y-auto rounded-lg border border-default bg-surface-0 shadow-lg"
          style={{
            top: position.openUpward ? undefined : position.top,
            bottom: position.openUpward ? position.bottom : undefined,
            left: position.left,
            width: position.width,
            maxHeight: position.maxHeight,
            visibility: position.visible ? 'visible' : 'hidden',
          }}
        >
          {buscando && (
            <div className="flex items-center gap-2 px-3 py-2 text-ui-sm text-tertiary">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Buscando...
            </div>
          )}
          {!buscando && resultados.length === 0 && (
            <div className="px-3 py-2 text-ui-sm text-tertiary">Sin resultados</div>
          )}
          {!buscando && resultados.map((item) => (
            <button
              key={item.idProducto}
              type="button"
              onClick={() => handleSelect(item)}
              className="flex w-full items-start gap-2 px-3 py-2 text-left text-ui-sm hover:bg-surface-hover"
            >
              <span className="font-mono text-tertiary flex-shrink-0">{item.idProducto}</span>
              <span className="text-primary">{item.producto}</span>
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={triggerRef} className="relative">
      <Input
        id="codigoSunat"
        label="Código SUNAT"
        required={isFieldRequired('codigoSunat')}
        labelAction={
          <Tooltip text="Opcional. Busca por código o nombre en el catálogo UNSPSC (clasificador de bienes y servicios)." position="right">
            <button
              type="button"
              aria-label="Ayuda: Código SUNAT"
              className="inline-flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </Tooltip>
        }
        value={query}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => { if (resultados.length > 0) setOpen(true); }}
        onBlur={onBlur}
        leftIcon={buscando ? <Loader2 className="w-3.5 h-3.5 animate-spin text-tertiary" /> : <FileBadge2 className="w-3.5 h-3.5" />}
        rightIcon={
          query || (!buscando && showCheck) ? (
            <span className="flex items-center gap-1.5">
              {query && (
                <button
                  type="button"
                  onClick={handleClear}
                  aria-label="Limpiar código SUNAT"
                  title="Limpiar"
                  tabIndex={-1}
                  className="inline-flex items-center justify-center text-tertiary hover:text-secondary transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              {!buscando && showCheck ? renderCheck?.() : null}
            </span>
          ) : undefined
        }
        error={errors.codigoSunat}
        size="small"
        placeholder="Buscar por código o nombre..."
        className="font-mono"
        autoComplete="off"
      />
      {dropdown}
    </div>
  );
};

export const ProductExistenceTypeField: React.FC<ProductCodesFieldProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  onBlur,
  showCheck,
  renderCheck
}) => {
  if (!isFieldVisible('tipoExistencia')) return null;

  return (
    <Select
      id="tipoExistencia"
      label="Tipo de existencia"
      required={isFieldRequired('tipoExistencia')}
      labelAction={
        <Tooltip text="Clasificación del bien para control y reportes." position="right">
          <button
            type="button"
            aria-label="Ayuda: Tipo de existencia"
            className="inline-flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </Tooltip>
      }
      value={formData.tipoExistencia}
      onChange={(e) =>
        setFormData(prev => ({ ...prev, tipoExistencia: e.target.value as ProductFormData['tipoExistencia'] }))
      }
      onBlur={onBlur}
      rightIcon={showCheck ? renderCheck?.() : undefined}
      error={errors.tipoExistencia}
      size="small"
    >
      <option value="MERCADERIAS">Mercaderías</option>
      <option value="PRODUCTOS_TERMINADOS">Productos Terminados</option>
      <option value="MATERIAS_PRIMAS">Materias Primas</option>
      <option value="ENVASES">Envases</option>
      <option value="MATERIALES_AUXILIARES">Materiales Auxiliares</option>
      <option value="SUMINISTROS">Suministros</option>
      <option value="REPUESTOS">Repuestos</option>
      <option value="EMBALAJES">Embalajes</option>
      <option value="OTROS">Otros</option>
    </Select>
  );
};


